import React, { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { toast } from "react-toastify";
import Sidebar from "./ChatWindow/Sidebar";
import TokenDisplay from "./ChatWindow/TokenDisplay";
import DatabaseSelector from "./ChatWindow/DatabaseSelector";
import ThemeToggle from "./ThemeToggle";
import api from "../apiWithAccessToken";
import {
  readScopedValue,
  removeScopedValue,
  resetAppStorageForLogout,
  writeScopedValue,
} from "../utils/scopedStorage";
import { resolveApiRoot } from "../apiConfig";
import "./DashboardLayout.css";

const toAbsoluteAssetUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const apiRoot = resolveApiRoot();
  if (!apiRoot) return url;
  return `${apiRoot}${url.startsWith("/") ? "" : "/"}${url}`;
};

const resolveAvatar = (payload, fallbackAvatar = null) => {
  const candidates = [payload?.profile_image, payload?.avatar_url, payload?.avatar];
  const firstNonEmpty = candidates.find((value) => typeof value === "string" && value.trim() !== "");

  if (firstNonEmpty) return toAbsoluteAssetUrl(firstNonEmpty);

  const hasExplicitEmpty = candidates.some((value) => value === null || value === "");
  if (hasExplicitEmpty) return null;

  return fallbackAvatar || null;
};

const isDownloadAllowed = (value) => {
  if (typeof value === "boolean") return value;
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  return ["1", "y", "yes", "true"].includes(normalized);
};

const buildUserState = (payload, fallbackUser = {}) => {
  const userPayload = payload?.user || payload || {};

  return {
    name: userPayload?.name || fallbackUser.name || "Guest",
    avatar: resolveAvatar(userPayload, fallbackUser.avatar ?? null),
    role: userPayload?.platform_role || userPayload?.role || fallbackUser.role || "",
    canDownload:
      "is_download_allowed" in userPayload
        ? isDownloadAllowed(userPayload?.is_download_allowed)
        : fallbackUser.canDownload ?? false,
  };
};

const resolveSelectedDatabaseId = (databases, candidateId) => {
  const normalizedCandidate =
    candidateId === null || candidateId === undefined ? "" : String(candidateId);
  const hasCandidate = databases.some((db) => String(db.id) === normalizedCandidate);

  if (hasCandidate) {
    return normalizedCandidate;
  }

  const fallbackId = databases[0]?.id;
  return fallbackId === null || fallbackId === undefined ? "" : String(fallbackId);
};

const DashboardLayout = ({ onLogout, initialProfile = null, storageScope = null }) => {
  const initialDatabases = Array.isArray(initialProfile?.databases) ? initialProfile.databases : [];
  const [user, setUser] = useState(() =>
    buildUserState(initialProfile, {
      name: "Guest",
      avatar: null,
      role: "",
      canDownload: false,
    })
  );
  const [databases, setDatabases] = useState(initialDatabases);
  const [selectedDatabaseId, setSelectedDatabaseId] = useState(() =>
    resolveSelectedDatabaseId(
      initialDatabases,
      readScopedValue(storageScope, "selected_database_id")
    )
  );
  const idleTimerRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(
    () => readScopedValue(storageScope, "session_id") || null
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [runtimeInfo, setRuntimeInfo] = useState(initialProfile?.runtime || null);
  const [llmRuntimeInfo, setLlmRuntimeInfo] = useState(initialProfile?.llm_runtime || null);
  const [quotaPolicy, setQuotaPolicy] = useState(initialProfile?.quota_policy || null);
  const [setupStatus, setSetupStatus] = useState(initialProfile?.setup_status ?? null);

  const navigate = useNavigate();
  const location = useLocation();

  const syncProfileState = useCallback(
    (payload, { syncWorkspace = false } = {}) => {
      const nextDatabases = Array.isArray(payload?.databases) ? payload.databases : [];

      setRuntimeInfo(payload?.runtime || null);
      setLlmRuntimeInfo(payload?.llm_runtime || null);
      setQuotaPolicy(payload?.quota_policy || null);
      setSetupStatus(payload?.setup_status ?? null);
      setDatabases(nextDatabases);
      setUser((prev) => buildUserState(payload, prev));

      if (syncWorkspace) {
        setSelectedDatabaseId((previousId) => {
          const storedId = readScopedValue(storageScope, "selected_database_id");
          return resolveSelectedDatabaseId(nextDatabases, previousId || storedId || "");
        });
        setActiveSessionId((previousId) => previousId || readScopedValue(storageScope, "session_id") || null);
      }
    },
    [storageScope]
  );

  const hydrateCurrentUser = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      syncProfileState(response?.data || {}, { syncWorkspace: true });
    } catch (error) {
      console.error("Failed to fetch user profile", error);
    }
  }, [syncProfileState]);

  const refreshCurrentUser = useCallback(async () => {
    await hydrateCurrentUser();
  }, [hydrateCurrentUser]);

  const handleLogout = useCallback(
    (reason = "manual") => {
      resetAppStorageForLogout();

      if (reason === "idle") {
        toast.info("You were logged out due to inactivity");
      } else {
        toast.success("Logged out successfully!");
      }

      if (onLogout) onLogout();
      navigate("/login", { replace: true });
    },
    [navigate, onLogout]
  );

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      handleLogout("idle");
    }, 15 * 60 * 1000);
  }, [handleLogout]);

  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

    events.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer));
    resetIdleTimer();

    return () => {
      events.forEach((eventName) =>
        window.removeEventListener(eventName, resetIdleTimer)
      );
      clearTimeout(idleTimerRef.current);
    };
  }, [resetIdleTimer]);

  useEffect(() => {
    if (!initialProfile) {
      hydrateCurrentUser();
      return;
    }

    syncProfileState(initialProfile, { syncWorkspace: true });
  }, [hydrateCurrentUser, initialProfile, syncProfileState]);

  useEffect(() => {
    if (!storageScope) return;

    if (!selectedDatabaseId) {
      removeScopedValue(storageScope, "selected_database_id");
      return;
    }

    writeScopedValue(storageScope, "selected_database_id", selectedDatabaseId);
  }, [selectedDatabaseId, storageScope]);

  useEffect(() => {
    if (!storageScope) return;

    if (!activeSessionId) {
      removeScopedValue(storageScope, "session_id");
      return;
    }

    writeScopedValue(storageScope, "session_id", activeSessionId);
  }, [activeSessionId, storageScope]);

  const handleSelectChat = (session) => {
    if (!session?.id) return;

    setActiveSessionId(String(session.id));

    if (session?.source_db_id !== null && session?.source_db_id !== undefined) {
      setSelectedDatabaseId(String(session.source_db_id));
    }

    if (location.pathname !== "/chatwindow") {
      navigate("/chatwindow");
    }
  };

  const handleNewChat = (session) => {
    setActiveSessionId(String(session.id));
    if (location.pathname !== "/chatwindow") {
      navigate("/chatwindow");
    }
  };

  const handleSessionCreated = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const sidebarRefreshRef = useRef(null);

  const refreshSessions = () => {
    sidebarRefreshRef.current?.();
  };

  const handleActiveSessionChange = useCallback((nextSessionId) => {
    if (!nextSessionId) return;
    setActiveSessionId(String(nextSessionId));
  }, []);

  const handleDatabaseChange = useCallback(
    (databaseId) => {
      const nextId = databaseId ?? "";
      const currentId = selectedDatabaseId ?? "";
      if (String(nextId) === String(currentId)) return;

      setSelectedDatabaseId(nextId ? String(nextId) : "");

      const inThreadView =
        location.pathname === "/chatwindow" &&
        !!activeSessionId &&
        !String(activeSessionId).startsWith("temp_");

      if (inThreadView) {
        const tempSessionId = `temp_${Date.now()}`;
        setActiveSessionId(tempSessionId);
        if (location.pathname !== "/chatwindow") {
          navigate("/chatwindow");
        }
      }
    },
    [activeSessionId, location.pathname, navigate, selectedDatabaseId]
  );

  const isThreadView =
    location.pathname === "/chatwindow" &&
    !!activeSessionId &&
    !String(activeSessionId).startsWith("temp_");
  const isDashboardView = location.pathname === "/dashboard";

  return (
    <div className="dashboard">
      <Sidebar
        refreshTrigger={refreshTrigger}
        user={user}
        storageScope={storageScope}
        onUserUpdated={setUser}
        refreshCurrentUser={refreshCurrentUser}
        onLogout={handleLogout}
        selectedDatabaseId={selectedDatabaseId}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        activeSessionId={activeSessionId}
        registerRefresh={(fn) => {
          sidebarRefreshRef.current = fn;
        }}
      />

      <div className="main">
        {location.pathname !== "/settings" && location.pathname !== "/datadump" && (
          <header className="app-header">
            <button
              className="mobile-menu-btn md:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <Menu size={22} />
            </button>

            <div className="titles">
              <h1 className="app-title">Geneilytics</h1>
              <p className="app-subtitle">Powered by Generative AI</p>
            </div>

            <div className="token-display-wrapper">
              {(isThreadView || isDashboardView) && databases.length > 0 && (
                <DatabaseSelector
                  className="header-db-selector"
                  variant="compact"
                  labelPrefix={isDashboardView ? "Dashboard DB:" : "Connected to:"}
                  databases={databases}
                  selectedDatabaseId={selectedDatabaseId}
                  onChange={handleDatabaseChange}
                />
              )}
              <TokenDisplay
                userRole={user?.role}
                runtime={runtimeInfo}
                quotaPolicy={quotaPolicy}
              />
              <ThemeToggle />
            </div>
          </header>
        )}

        <div className="main-content">
          <div className="dashboard-content">
            <Outlet
              context={{
                user,
                canDownload: Boolean(user?.canDownload),
                sidebarOpen,
                setSidebarOpen,
                sidebarCollapsed,
                activeSessionId,
                onActiveSessionChange: handleActiveSessionChange,
                databases,
                selectedDatabaseId,
                setupStatus,
                llmRuntimeInfo,
                storageScope,
                onSelectDatabase: handleDatabaseChange,
                onSelectChat: handleSelectChat,
                onSessionCreated: handleSessionCreated,
                refreshSessions,
                refreshCurrentUser,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
